import { FlowType } from "conversion_flows/types/scalars"
import posthog from "posthog-js"
import { ObjectValues } from "types/utility"
import {
  Action,
  DEPRECATED__DO_NOT_USE__PRODUCT,
  Entity,
  EVENT_TYPE,
  EventType,
} from "website_script/lib/events"
import { listenToIframeMessage } from "website_script/lib/iframeMessaging"

export const DEFAULT_POSTHOG_KEY =
  "phc_1cgcV85QBSU1J9gMqEPP2A4Qw7ysHMc92cyp6T4DHPB"

export type TrackingPayload = {
  entity: Entity
  element?: string
  action: Action
  details?: string
  type: EventType
  // list option is useful for tracking displayed ctas
  // in widgets that display multiple ctas
  flow?: FlowType | FlowType[]
  carRating?: number
}

type EventsContext = {
  country: string
  customerId: string
  customerName: string
  departmentId: string
}

class AuTracker {
  enabled?: boolean
  context?: EventsContext

  init(isEnabled: boolean, context: EventsContext) {
    this.enabled = isEnabled

    if (!this.enabled) return

    this.context = context

    listenToIframeMessage(
      ({ kind, ...data }: TrackingPayload & { kind: string }) => {
        if (kind !== "AuTracker.eventFromIframe") return
        this.track(data)
      },
      /.*/, // TODO: should we change this to a more restrictive regex?
      true
    )
    // TODO: should we make the token configurable?
    // dev vs test vs prod?
    posthog.init(DEFAULT_POSTHOG_KEY, {
      api_host: "https://evt.autouncle.com",
      person_profiles: "identified_only",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      persistence: "sessionStorage",
      // debug: true,
    })
  }

  validateData(payload: TrackingPayload & { name: string }): boolean {
    return (
      typeof payload.name === "string" &&
      payload.name !== "" &&
      Object.values(EVENT_TYPE).includes(payload.type)
      // TODO: extend checks?
    )
  }

  _track(payload: TrackingPayload): void {
    const { entity, action, type, flow, element, details, carRating } = payload
    if (!this.enabled) return
    if (!this.context) {
      console.warn("No context has been provided to the AuTracker!")
      return
    }

    const name = [entity, element, action].filter(Boolean).join(" ")

    if (!this.validateData({ ...payload, name })) {
      console.info("data seems corrupt", payload)
      return
    }

    const posthogFlow = Array.isArray(flow) ? flow.join(",") : flow

    posthog.capture(name, {
      entity,
      action,
      type,
      flow: posthogFlow,
      element,
      details,
      carRating,
      customer_id: this.context.customerId,
      customer_name: this.context.customerName,
      department_id: this.context.departmentId,
      country: this.context.country,
    })
  }

  // Temporary wrapper to bypass some tracking calls
  // Check this._track for actual implementation
  track(payload: TrackingPayload): void
  track(
    name: string,
    type?: EventType,
    product?: ObjectValues<typeof DEPRECATED__DO_NOT_USE__PRODUCT>
  ): void
  track(
    payloadOrName: TrackingPayload | string,
    type?: EventType,
    product?: ObjectValues<typeof DEPRECATED__DO_NOT_USE__PRODUCT>
  ) {
    // Disregard tracking call for events that have not been updated yet
    // (old function signature)
    if (typeof payloadOrName === "string")
      return `${payloadOrName} ${type} ${product}` && undefined

    return this._track(payloadOrName)
  }
}

const auTracker = new AuTracker()

export default auTracker

export const trackFunction = auTracker.track.bind(auTracker)
